/**
 * Finding a league among the fifty-four, and the source that publishes it.
 *
 * Add Source used to ask for a publisher and then for a league. The client
 * reported the consequence rather than the cause: they did not know which
 * source carried the league they wanted, so finding out meant opening each of
 * ten sources in turn and reading its menu. Two publishers carry NCAA Division
 * II football. Nothing on the screen said so.
 *
 * So the screen asks for a league first, and this module is the part of that
 * worth asserting: a term, a facet, an order, and what choosing a league does
 * to the Source control beside it. The menu itself comes from the server
 * (`app/domain/league_menu.py`), which is the only thing that can know which
 * parameter on which template reaches which league.
 *
 * **Three rules carried over from the Sources list, for the same reasons.**
 *
 * - *What is searched is what is printed* — `stat-search.ts` states it and
 *   `source-list.ts` keeps it. A row matches on its league name, its sport, and
 *   the publishers under it, all three of which the row shows. It also matches
 *   on the labels of the choices behind a source, which it does **not** show
 *   until that source is chosen, and that is the one deliberate exception: the
 *   league named `nba_regular_season` is reached by three Cerebro scopes called
 *   *NBA 2023-24 Regular Season* and so on, and a reader who types a year has
 *   named something real about the row. See `searchable`.
 * - *A `<select>` whose value matches none of its options renders blank* — this
 *   frontend's most-repeated defect. `offering` and `chooseSport` in
 *   `source-list.ts` exist for it; `sourceFor` and `narrowBySport` here are the
 *   same guard on the two controls this screen gained.
 * - *Narrowing returns the same array when nothing was dropped*, so a screen
 *   that re-renders does not restart every memo keyed on the list.
 *
 * **And one rule that is this module's own: search is ranked, not merely
 * filtered.** Fifty-four leagues is past the point where a filtered list reads
 * as an answer — typing `ncaa` leaves eleven of them, and the one whose name
 * *is* what was typed should not be ninth. `rank` puts a name that starts with
 * the term above a name that merely contains it, and both above a row matched
 * only by its publisher. Within a tier the server's order is kept, because the
 * menu has no better one to impose.
 *
 * Free of React and of every runtime import, like `selection.ts` and
 * `source-list.ts`: `npm test` runs these modules through bare
 * `node --experimental-strip-types`, which resolves ES modules by file and
 * extension, and every import inside `src/` is written extensionless for Vite.
 * A value import from one `src` module to another makes this unloadable under
 * the test runner. Types are erased and may be imported.
 */

import type {
  LeagueMenuEntry,
  LeagueSource,
  SourceTemplate,
  TemplateValues,
} from "./api";

// --- The text rule ---------------------------------------------------------

/**
 * Case, spacing and punctuation folded away.
 *
 * `source-list.ts`'s `fold`, copied under the constraint that file documents at
 * length — a runtime import between `src` modules is unloadable under the test
 * runner. Pinned rather than trusted: `tests/league-menu.test.ts` drives this
 * and `source-list.ts`'s over the same inputs and fails if they disagree.
 *
 * It is what makes `ncaa_d2`, `NCAA D2` and `ncaa-d2` one search term, which
 * matters more here than on the Sources list: these names come from ten
 * different publishers and are not spelled to one convention.
 */
export function fold(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/**
 * A typed query as the terms it holds — `source-list.ts`'s `words`, copied on
 * the same terms and pinned by the same test.
 *
 * Split rather than folded whole, so terms may be matched in any order and
 * against different fields: "pff d2" finds Division II by its publisher and its
 * name together, which one folded string could not.
 */
export function words(query: string): string[] {
  return query
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((term) => term !== "");
}

// --- Narrowing -------------------------------------------------------------

/**
 * The strings one league is found by.
 *
 * Four kinds, and the last is the exception to *what is searched is what is
 * printed*:
 *
 * - **The name the row prints, the canonical league name, and its key.** All
 *   three, because they differ and the reader may have any of them in mind:
 *   the row leads with *NCAA Division II Football*, the Sources table prints
 *   `ncaa_d2` for the profile it becomes, and the published file is named from
 *   the slug.
 * - **The sport**, which the row shows beside the name.
 * - **Each publisher's name and its template's name.** This is the whole
 *   feature: typing `pff` should list the four leagues PFF publishes. The row
 *   shows them.
 * - **Each choice's label**, which the row does not show. The justification is
 *   that the label names something real about the row that its own name cannot
 *   — Cerebro's three *Regular Season* scopes share the league
 *   `nba_regular_season` — so a reader typing `2025` has described a row rather
 *   than guessed at one. The alternative is a search that cannot find a season
 *   the publisher advertises by season.
 */
function searchable(entry: LeagueMenuEntry): readonly string[] {
  // The printed name **and** the canonical one. Both are on the row and they
  // differ on nine leagues: somebody who read `ncaa_d2` off the Sources table
  // must find it by typing that, and somebody reading this menu must find
  // *NCAA Division II Football* by typing "division".
  const text = [
    fold(entry.display),
    fold(entry.league),
    fold(entry.key),
    fold(entry.sport),
  ];
  for (const source of entry.sources) {
    text.push(fold(source.publisher), fold(source.template_name));
    for (const choice of source.choices) text.push(fold(choice.label));
  }
  return text.filter((value) => value !== "");
}

/** Whether every term lands somewhere on the row. */
function matches(entry: LeagueMenuEntry, terms: readonly string[]): boolean {
  const text = searchable(entry);
  return terms.every((term) => text.some((value) => value.includes(term)));
}

/**
 * How well a row answers the query: 0 best, 2 worst.
 *
 * Judged on the **first** term alone, and on the league's own name and key
 * only. Two reasons, and they pull the same way: the first term is what
 * somebody typed before they started narrowing, so it is the one most likely to
 * be the thing they are naming; and a tier computed from every term over every
 * field would have the strongest match be whichever row happened to carry the
 * query's words in the most places, which is not a judgement a reader could
 * predict or would agree with.
 *
 * - **0** — the name begins with it. `ncaa` puts `ncaa_d2` at the top.
 * - **1** — the name contains it. `d2` finds `ncaa_d2` and `ncaa_d2_soccer_men`.
 * - **2** — matched by something else: a publisher, a choice's label. `pff`
 *   lists four leagues, none of which is called PFF.
 */
function rank(entry: LeagueMenuEntry, terms: readonly string[]): number {
  const first = terms[0];
  if (first === undefined) return 0;
  // Ranked on the three names the row *is*, not on its publishers — so "ncaa"
  // leads with the leagues called NCAA something, and a league merely
  // published by the NCAA template sorts under them.
  const names = [fold(entry.display), fold(entry.league), fold(entry.key)];
  if (names.some((name) => name.startsWith(first))) return 0;
  if (names.some((name) => name.includes(first))) return 1;
  return 2;
}

/** A typed term and the sport facet, as the screen holds them. */
export interface LeagueNarrowing {
  query: string;
  /** A sport, or the empty string for every sport. */
  sport: string;
}

/** Narrowing by nothing: what the control opens on. */
export const EVERY_LEAGUE: LeagueNarrowing = { query: "", sport: "" };

/**
 * The leagues a narrowing keeps, best answers first.
 *
 * Returns the **same array** when nothing was dropped and nothing was
 * reordered, as `source-list.ts`'s `narrow` does and for the same reason: this
 * runs on every keystroke of a control inside a screen that also polls, and a
 * fresh array each time restarts every memo keyed on the list.
 *
 * A query of nothing but spaces and punctuation yields no terms and is
 * therefore not a filter, which is what keeps a stray keystroke from emptying
 * the menu.
 *
 * The sort is stable by specification, so rows sharing a tier keep the server's
 * order — sport first, then league name — rather than shuffling as a term is
 * typed out.
 */
export function narrowLeagues(
  entries: readonly LeagueMenuEntry[],
  narrowing: LeagueNarrowing,
): readonly LeagueMenuEntry[] {
  const terms = words(narrowing.query);
  if (terms.length === 0 && narrowing.sport === "") return entries;

  const kept = entries.filter((entry) => {
    if (narrowing.sport !== "" && entry.sport !== narrowing.sport) return false;
    return terms.length === 0 || matches(entry, terms);
  });
  if (terms.length === 0) return kept.length === entries.length ? entries : kept;
  return [...kept].sort((left, right) => rank(left, terms) - rank(right, terms));
}

/**
 * The sports the league menu can be filtered by.
 *
 * Exactly the sports the menu holds a league for, in the order the server sent
 * them — which is the naming standard's §7 order, because that is the order
 * `league_menu.menu()` sorts in. Taking it off the rows rather than off
 * `SPORTS` is what keeps the control from offering a sport that empties the
 * menu, which is a filter reporting a hole in the portal where it means to
 * report nothing at all.
 *
 * **Empty when there is nothing to choose between**, which the caller reads as
 * "render no control" — the convention `sportsOffered` set. One sport beside
 * *All sports* is two ways to spell the same list, and a filter that cannot
 * narrow anything still reads as a setting somebody has to consider.
 */
export function leagueSportOptions(
  entries: readonly LeagueMenuEntry[] | null | undefined,
): readonly string[] {
  const seen: string[] = [];
  for (const entry of entries ?? []) {
    if (entry.sport !== "" && !seen.includes(entry.sport)) seen.push(entry.sport);
  }
  return seen.length > 1 ? seen : [];
}

/**
 * Choosing a sport, and what it does to the league beside it.
 *
 * A league belongs to exactly one sport, so changing the sport usually strands
 * the league on a value the new sport has no row for — an empty menu caused by
 * a control the reader did not touch. `source-list.ts`'s `chooseSport` answers
 * this for the Sources table and this is the same arithmetic one screen over,
 * with one difference that matters: there, a stranded league left an empty
 * **table**, which is recoverable by reading it. Here it would leave a Source
 * dropdown holding a template chosen for a league that is no longer on screen,
 * and a composition still running against it.
 *
 * So the league is dropped unless the new sport actually has it. The caller
 * drops the source with it — see `sourceFor`, which returns null for a league
 * of "".
 */
export function chooseSport(
  entries: readonly LeagueMenuEntry[],
  league: string,
  sport: string,
): string {
  if (sport === "" || league === "") return league;
  const chosen = entries.find((entry) => entry.key === league);
  return chosen !== undefined && chosen.sport === sport ? league : "";
}

// --- The league, and the source under it -----------------------------------

/** The menu row for a league key, or null for none — including "". */
export function leagueFor(
  entries: readonly LeagueMenuEntry[] | null | undefined,
  league: string,
): LeagueMenuEntry | null {
  if (league === "") return null;
  return (entries ?? []).find((entry) => entry.key === league) ?? null;
}

/**
 * Which source to show for a league, given what was already chosen.
 *
 * The rule is **keep the publisher if it still applies**, and it is the point
 * of having a Source control at all. Somebody comparing PFF's Division II with
 * the NCAA template's switches between two leagues that both publishers carry;
 * re-answering the publisher question at every switch would make the comparison
 * the thing the screen is worst at. Where the publisher does not carry the new
 * league there is nothing to keep, and the first source is the server's order,
 * which is the registry's.
 *
 * Null only when the league itself is not on the menu — a key from an address
 * bar, or a menu one poll out of date. The caller renders no Source control
 * rather than an empty one.
 */
export function sourceFor(
  entries: readonly LeagueMenuEntry[] | null | undefined,
  league: string,
  templateKey: string,
): LeagueSource | null {
  const entry = leagueFor(entries, league);
  if (entry === null) return null;
  const kept = entry.sources.find((source) => source.template_key === templateKey);
  return kept ?? entry.sources[0] ?? null;
}

/**
 * The parameter values picking this source for this league settles.
 *
 * Empty for a publisher with one league and no choice to make, which is the
 * right answer rather than a missing one: `templateDefaults` seeds nothing and
 * every control opens where the declaration says.
 */
export function selectsFor(
  entries: readonly LeagueMenuEntry[] | null | undefined,
  league: string,
  templateKey: string,
): TemplateValues {
  return sourceFor(entries, league, templateKey)?.selects ?? {};
}

/**
 * What to say under the Source control about how many publishers reached here.
 *
 * Null when there is nothing worth saying — one publisher, one way in, which is
 * forty-nine of the fifty-four rows. A caption on every row would be noise, and
 * noise is what made the count invisible on the rows that have something to
 * report.
 *
 * Two different facts, and they are separate because they are answered by
 * different controls:
 *
 * - **Several publishers** is answered by the Source dropdown itself, so the
 *   caption's job is to say the dropdown is worth opening.
 * - **Several choices within one publisher** is answered by that template's own
 *   parameter control further down the form, which is already on screen and
 *   already seeded. The caption names which one was picked, because the form
 *   picked it rather than the reader, and an unremarked choice is the kind that
 *   is discovered after a Run.
 */
export function sourceNote(
  entries: readonly LeagueMenuEntry[] | null | undefined,
  league: string,
  templateKey: string,
): string | null {
  const entry = leagueFor(entries, league);
  const source = sourceFor(entries, league, templateKey);
  if (entry === null || source === null) return null;

  const notes: string[] = [];
  if (entry.sources.length > 1) {
    notes.push(`${entry.sources.length} sources publish ${entry.league}.`);
  }
  // `choices[0]` is read through a binding rather than indexed twice, so the
  // length test above is what proves it exists — `noUncheckedIndexedAccess` is
  // on, and it is right to be: a server sending a source with no choices at all
  // would otherwise put the string "undefined" in front of the reader.
  const first = source.choices[0];
  if (first !== undefined && source.choices.length > 1) {
    notes.push(
      `${source.publisher} reaches it ${source.choices.length} ways — this one is ` +
        `“${first.label}”, and the ${source.publisher} settings below change it.`,
    );
  }
  return notes.length > 0 ? notes.join(" ") : null;
}

/**
 * What to say after a source's name about the credential it needs.
 *
 * Three different sentences, because there are three different situations and
 * the row said "(needs a sign-in)" for all of them:
 *
 * - **A sign-in** — an email and a password, submitted to the publisher, proved
 *   on a worker because a browser login takes up to ninety seconds (ADR-0033).
 *   "(needs a sign-in)" is right, and it was right when every credentialed
 *   source was one of these.
 * - **An API key this deployment does not have** — nothing is submitted to
 *   anybody and there is no form to sign into, so calling it a sign-in sends a
 *   subscriber looking for a login page that will not help them.
 * - **An API key this deployment already has** — nothing is needed at all, and
 *   a row saying something is needed is simply false.
 *
 * The third is what made this worth separating. ADR-0059 put two PFF sources on
 * the FBS row, one behind a browser sign-in and one behind a configured key, and
 * the whole point of the two rows is that they differ in the credential — so a
 * suffix that said the same thing about both removed the only information the
 * reader needed.
 *
 * Returns the empty string rather than null where there is nothing to add, so
 * the call site can interpolate it without a conditional.
 */
export function credentialNote(
  source: LeagueSource,
  templates: readonly SourceTemplate[] | null | undefined,
): string {
  if (!source.needs_credential) return "";
  // The menu row does not carry the credential's type — it is a fact about the
  // template rather than about reaching a league — so it is joined here. An
  // absent catalogue falls back to the sign-in wording, which is what every
  // credentialed source but two actually needs.
  const template = (templates ?? []).find(
    (entry) => entry.key === source.template_key,
  );
  if (template?.credential_type !== "bearer_token") return " (needs a sign-in)";
  return template.credential_preconfigured === true
    ? " (API key configured)"
    : " (needs an API key)";
}
